import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';

// Policy ids look like `<account>-<line>-<term>-<carrier>` (e.g. "996934720-auto-002-BX"); the
// first two segments are the stable "stem" that identifies one policy across all of its terms.
const stemOf = (id) => (id ? id.split('-').slice(0, 2).join('-') : null);

// A renewal term that was cancelled (or is mid-cancellation) never took effect, so it must not
// drive the snapshot — otherwise an in-force term would point at a dead renewal's date/score, the
// exact contradiction this change removes. We deliberately keep `Inactive` (already-expired)
// renewals: when staff view an older historical term, its real renewal is now expired and is still
// the correct one to reference.
const CANCELLED_RENEWAL_STATUSES = new Set([PolicyStatus.Cancelled, PolicyStatus.InCancellation]);

/**
 * Decide what the Community Drive detail hint should show for a given policy term & driver (#32093).
 *
 * Pure helper — every input is passed in, no store access — so the term-position logic is unit
 * testable. Every term on the account is loaded with the full PolicyFragment (via GET_ACCOUNT_POLICY),
 * so the renewal term's `rateControlDate` and per-driver `communityDriveScore` are already present in
 * `policiesList`; the snapshot and not-effective modes need no extra fetch.
 *
 *  - 'renewalSnapshot' a later renewal term already exists -> show the score captured when that
 *                      renewal generated (that snapshot, not today's live score, set its discount)
 *  - 'notEffective'    this is the most recent term and it hasn't started yet -> nothing to show
 *  - 'live'            most recent term, in force -> the live in-app score (fetched from BigQuery)
 */
export const getCommunityDriveDetailMode = ({ currentPolicy, policiesList = [], driverId }) => {
  const stem = stemOf(currentPolicy?.id);
  const renewalTerm = stem
    ? policiesList
        .filter(
          (p) =>
            stemOf(p.id) === stem && p.term > currentPolicy?.term && !CANCELLED_RENEWAL_STATUSES.has(getPolicyStatus(p))
        )
        .sort((a, b) => a.term - b.term)[0]
    : undefined;

  if (renewalTerm) {
    return {
      mode: 'renewalSnapshot',
      // rateControlDate is the as-of date the renewal's rating (incl. its CD score) was captured.
      renewalGeneratedDate: renewalTerm.rateControlDate,
      renewalScore: renewalTerm.policyDetails?.drivers?.find((d) => d.id === driverId)?.communityDriveScore
    };
  }

  if (currentPolicy && getPolicyStatus(currentPolicy) === PolicyStatus.Future) {
    return { mode: 'notEffective' };
  }

  return { mode: 'live' };
};
